import { ArrowRight } from 'lucide-react'
import {
  MessageSquare,
  GitBranch,
  CheckCircle2,
  Package,
  Flag,
} from 'lucide-react'
import { cn } from '@/utils/cn'
import { formatRelativeTime } from '@/utils/formatters'
import { getNotificationAction } from '@/utils/notificationActions'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import type {
  NotificationRead,
  NotificationCategory,
} from '@/types/notification'

const categoryConfig: Record<
  NotificationCategory,
  { icon: React.ElementType; color: string }
> = {
  CHAT: { icon: MessageSquare, color: 'text-blue-500' },
  WORKFLOW: { icon: GitBranch, color: 'text-purple-500' },
  APPROVAL: { icon: CheckCircle2, color: 'text-green-500' },
  SOURCING: { icon: Package, color: 'text-orange-500' },
  MILESTONE: { icon: Flag, color: 'text-red-500' },
}

interface NotificationItemProps {
  notification: NotificationRead
  compact?: boolean
  onClick?: (notification: NotificationRead) => void
  onActionClick?: (
    notification: NotificationRead,
    url: string,
    state?: Record<string, unknown>
  ) => void
}

export function NotificationItem({
  notification,
  compact = false,
  onClick,
  onActionClick,
}: NotificationItemProps) {
  const config = categoryConfig[notification.category]
  const Icon = config.icon
  const action = getNotificationAction(notification)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick?.(notification)
    }
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(notification)}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex w-full items-start gap-3 text-left cursor-pointer transition-colors hover:bg-muted/50',
        compact ? 'px-3 py-2.5' : 'px-4 py-3',
        !notification.is_read && 'bg-blue-50/50'
      )}
    >
      <div
        className={cn('mt-0.5 flex-shrink-0', compact ? 'h-4 w-4' : 'h-5 w-5')}
      >
        <Icon className={cn('h-full w-full', config.color)} />
      </div>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-sm font-medium text-foreground',
            compact && 'text-xs'
          )}
        >
          {notification.title}
        </p>
        <TooltipProvider delayDuration={2000}>
          <Tooltip>
            <TooltipTrigger asChild>
              <p
                className={cn(
                  'text-muted-foreground truncate',
                  compact ? 'text-xs' : 'text-sm'
                )}
              >
                {notification.message}
              </p>
            </TooltipTrigger>
            <TooltipContent
              side="bottom"
              align="start"
              alignOffset={-8}
              sideOffset={-9}
              collisionPadding={12}
              arrowClassName="bg-red-50 fill-red-50"
              className="max-w-[calc(100vw-1.5rem)] overflow-x-auto whitespace-nowrap rounded-lg border border-red-100 bg-red-50 px-3 py-0.5 text-sm font-normal text-red-600 shadow-sm [&::before]:hidden [&::after]:hidden"
            >
              {notification.message}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="mt-0.5 flex items-center justify-between">
          <p
            className={cn(
              'text-muted-foreground',
              compact ? 'text-[10px]' : 'text-xs'
            )}
          >
            {formatRelativeTime(notification.created_at)}
          </p>
          {action && (
            <Button
              variant="link"
              size="sm"
              className={cn(
                'h-auto py-0 px-1 text-primary',
                compact ? 'text-[11px]' : 'text-xs'
              )}
              onClick={e => {
                e.stopPropagation()
                onActionClick?.(notification, action.url, action.state)
              }}
            >
              {action.label}
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {!notification.is_read && (
        <div className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
      )}
    </div>
  )
}
